/**
 * Section registry — canonical brief specs + registry metadata for the
 * nine workspace sections (Operations, Design, Hiring, Development,
 * Marketing, Sales, Customer Success, Finance, Legal).
 *
 * One file. Data only — no React, no icons (those live in the UI
 * companion at packages/ui/src/blocks/sections/section-registry-ui.tsx).
 *
 * Consumers:
 *   • packages/excellent-mcp/src/tools/section-briefs.ts — schema
 *     validation, section_seed_apply, section_audit tools.
 *   • excellent/src/sections/db.ts — completeness scoring + audits.
 *   • excellent/src/sections/scoring.ts — composite section score.
 *   • excellent/src/projects/ensure.ts — per-project section enablement.
 *   • packages/ui/src/blocks/sections/* — UX field specs.
 *
 * Lockstep mirror: packages/excellent-mcp/src/section-registry.ts. Body
 * must stay byte-identical (the doc header may differ). Enforced by
 * `pnpm check-sections-lockstep`.
 *
 * Seed question sets are applied idempotently via the `_seeds` table
 * (key = `section_brief:<slug>`, version = `seedVersion` on the spec).
 * Bumping `seedVersion` re-seeds — additive only.
 */
// Hiring's display name is single-sourced in jtbd-registry.ts (decision D5 —
// one label, "Hiring", everywhere); imported rather than re-literaled here so
// the section registry and the department registry can never drift apart.
import { HIRING_DISPLAY_NAME } from "./jtbd-registry.js";
// ---------- Operations ----------
const OPERATIONS = {
    slug: "operations",
    label: "Operations",
    description: "The business beneath the workspace — who, what, where.",
    seedVersion: 1,
    fields: [
        {
            key: "legal_entity_name",
            label: "Legal entity name",
            prompt: "What's the legal name on the paperwork?",
            subhead: "The full registered name on your incorporation docs — e.g. \"Acme Holdings, LLC\", not the brand you market under.",
            placeholder: "e.g. Acme Holdings, LLC",
            type: "text",
            priority: 5,
            required: true,
        },
        {
            key: "doing_business_as",
            label: "Doing business as",
            prompt: "What trade name do you go to market under, if it's different?",
            subhead: "The customer-facing brand when it differs from the legal name. Leave blank if they're the same.",
            placeholder: "e.g. Acme",
            type: "text",
            priority: 2,
            required: false,
        },
        {
            key: "business_type",
            label: "Business type",
            prompt: "How is the business legally structured?",
            subhead: "The entity type on your formation paperwork — e.g. LLC, S-Corp, or sole proprietorship.",
            type: "select",
            priority: 4,
            required: true,
            options: [
                { value: "llc", label: "LLC" },
                { value: "c_corp", label: "C Corporation" },
                { value: "s_corp", label: "S Corporation" },
                { value: "partnership", label: "Partnership" },
                { value: "sole_prop", label: "Sole proprietorship" },
                { value: "nonprofit", label: "Nonprofit" },
                { value: "other", label: "Other" },
            ],
        },
        {
            key: "ein",
            label: "EIN / tax ID",
            prompt: "What's your federal tax ID?",
            subhead: "Your Employer Identification Number, or the local equivalent (e.g. VAT number). Stored privately.",
            type: "text",
            priority: 3,
            required: false,
            sensitive: true,
        },
        {
            key: "hq_address",
            label: "Headquarters address",
            prompt: "Where is the business headquartered?",
            subhead: "Your primary business address — street, city, state, and postal code.",
            placeholder: "Street, City, State ZIP",
            type: "longtext",
            priority: 5,
            required: true,
        },
        {
            key: "additional_locations",
            label: "Additional locations",
            prompt: "Do you have any other locations?",
            subhead: "Other offices, stores, or facilities — one per line. Leave blank if HQ is your only site.",
            type: "longtext",
            priority: 1,
            required: false,
        },
        {
            key: "hours",
            label: "Business hours",
            prompt: "When are you open or reachable?",
            subhead: "Your operating hours and time zone — e.g. \"Mon–Fri 9am–6pm ET\".",
            type: "longtext",
            priority: 2,
            required: false,
        },
        {
            key: "primary_services",
            label: "Primary services or products",
            prompt: "What does the business actually sell?",
            subhead: "Your core products or services, one per entry — e.g. \"Managed IT support\", \"Annual security audits\".",
            type: "multi",
            priority: 5,
            required: true,
        },
        {
            key: "departments",
            label: "Departments",
            prompt: "What functional groups make up the business?",
            subhead: "The teams inside the org — e.g. Sales, Engineering, Support, Finance.",
            type: "multi",
            priority: 3,
            required: true,
        },
        {
            key: "key_processes",
            label: "Key processes",
            prompt: "What are the 3-5 critical workflows you couldn't run without?",
            subhead: "The operational processes that keep the business going — e.g. \"order fulfillment\", \"client onboarding\", \"monthly close\".",
            type: "longtext",
            priority: 2,
            required: false,
        },
        {
            key: "ops_lead",
            label: "Operations lead",
            prompt: "Who's accountable for operations?",
            subhead: "The person who owns day-to-day operations and is the point of contact when something breaks.",
            type: "urn-person",
            priority: 4,
            required: true,
        },
        {
            key: "compliance_framework",
            label: "Compliance framework",
            prompt: "Which regulatory frameworks do you operate under?",
            subhead: "Industry or regional regimes you must comply with — e.g. SOC 2, HIPAA, PCI-DSS. Leave blank if none apply.",
            type: "multi",
            priority: 2,
            required: false,
        },
    ],
    seedSets: [
        {
            title: "Tell me about the business",
            questions: [
                {
                    question: "What is the legal name of the entity?",
                    subhead: "The full registered name on your incorporation paperwork — e.g. \"Acme Holdings, LLC\".",
                    briefKey: "legal_entity_name",
                },
                {
                    question: "What does the business actually sell — products, services, or both?",
                    subhead: "Name your core offerings the way a customer would describe them.",
                    briefKey: "primary_services",
                },
                {
                    question: "Where is the headquarters located?",
                    subhead: "Your primary business address — street, city, state, and postal code.",
                    briefKey: "hq_address",
                },
                {
                    question: "Who is the operations lead — the person accountable when something breaks?",
                    subhead: "Pick the workspace member who owns day-to-day operations.",
                    briefKey: "ops_lead",
                },
                {
                    question: "What are the 3-5 critical operational processes you couldn't run without?",
                    subhead: "The workflows that keep the business running — e.g. \"order fulfillment\", \"client onboarding\", \"monthly close\".",
                    briefKey: "key_processes",
                },
            ],
        },
    ],
};
// ---------- Design ----------
const DESIGN = {
    slug: "design",
    label: "Design",
    description: "Design system source of truth, audit cadence, and ownership.",
    seedVersion: 1,
    fields: [
        {
            key: "design_tool",
            label: "Design tool",
            prompt: "What tool does the team design in day-to-day?",
            subhead: "Your primary design surface — e.g. Figma, Sketch, or code-first if you design in the browser.",
            type: "select",
            priority: 5,
            required: true,
            options: [
                { value: "figma", label: "Figma" },
                { value: "sketch", label: "Sketch" },
                { value: "penpot", label: "Penpot" },
                { value: "framer", label: "Framer" },
                { value: "none", label: "None / code-first" },
                { value: "other", label: "Other" },
            ],
        },
        {
            key: "design_file_url",
            label: "Design file URL",
            prompt: "Where does the live design file live?",
            subhead: "Paste the link to the source-of-truth file other people open — e.g. a Figma project URL.",
            placeholder: "https://figma.com/file/...",
            type: "url",
            priority: 4,
            required: true,
        },
        {
            key: "token_source",
            label: "Token source",
            prompt: "Where do design tokens (color, type, spacing) live?",
            subhead: "The single source the team treats as canonical for tokens — design-tool variables, a JSON file, or code.",
            type: "select",
            priority: 4,
            required: true,
            options: [
                { value: "figma_vars", label: "Figma variables" },
                { value: "tokens_json", label: "Tokens JSON" },
                { value: "code", label: "Code (CSS / Tailwind)" },
                { value: "informal", label: "Informal / not codified" },
            ],
        },
        {
            key: "component_library_path",
            label: "Component library path",
            prompt: "Where do the implemented components live in the repo?",
            subhead: "The folder that holds your shared UI components — e.g. \"packages/ui/src\".",
            placeholder: "e.g. packages/ui/src",
            type: "text",
            priority: 3,
            required: true,
        },
        {
            key: "audit_cadence",
            label: "Audit cadence",
            prompt: "How often does the design system get reviewed?",
            subhead: "How frequently someone audits the system for drift and inconsistency.",
            type: "select",
            priority: 2,
            required: false,
            options: [
                { value: "weekly", label: "Weekly" },
                { value: "monthly", label: "Monthly" },
                { value: "quarterly", label: "Quarterly" },
                { value: "ad_hoc", label: "Ad hoc" },
            ],
        },
        {
            key: "design_lead",
            label: "Design lead",
            prompt: "Who owns design decisions?",
            subhead: "The workspace member who has final say on visual and UX direction.",
            type: "urn-person",
            priority: 4,
            required: true,
        },
        {
            key: "voice_principles",
            label: "Voice & tone principles",
            prompt: "How should the product speak?",
            subhead: "3-5 principles that define your voice — e.g. \"plain over clever\", \"confident, never pushy\".",
            type: "longtext",
            priority: 2,
            required: false,
        },
    ],
    seedSets: [
        {
            title: "Wire the design system",
            questions: [
                { question: "What design tool does the team use day-to-day?", subhead: "Your primary design surface — e.g. Figma, Sketch, or code-first.", briefKey: "design_tool" },
                { question: "Where does the live design file live? Paste the URL.", subhead: "The source-of-truth file others open — e.g. a Figma project link.", briefKey: "design_file_url" },
                { question: "Where are design tokens defined — Figma variables, JSON, or code?", subhead: "The canonical home for your color, type, and spacing tokens.", briefKey: "token_source" },
                { question: "Where do the implemented components live in the repo?", subhead: "The folder that holds your shared UI components — e.g. \"packages/ui/src\".", briefKey: "component_library_path" },
                { question: "Who is the design lead?", subhead: "Pick the workspace member who owns design decisions.", briefKey: "design_lead" },
            ],
        },
    ],
};
// ---------- Hiring ----------
const HIRING = {
    slug: "hiring",
    label: HIRING_DISPLAY_NAME,
    description: "Headcount goals, open roles, comp bands.",
    seedVersion: 1,
    fields: [
        {
            key: "target_headcount_eoy",
            label: "Target headcount (end of year)",
            prompt: "How many people do you want on the team by the end of the year?",
            subhead: "Your goal for total headcount at year end — a single number.",
            type: "number",
            priority: 5,
            required: true,
        },
        {
            key: "current_headcount",
            label: "Current headcount",
            prompt: "How many people are on the team today?",
            subhead: "Everyone on payroll right now, including founders.",
            type: "number",
            priority: 4,
            required: true,
        },
        {
            key: "hiring_timeline",
            label: "Hiring timeline",
            prompt: "What's the pace and key milestones for hiring this year?",
            subhead: "When you plan to open which roles — e.g. \"2 engineers by Q2, a designer by Q3\".",
            type: "longtext",
            priority: 3,
            required: false,
        },
        {
            key: "ats_choice",
            label: "ATS choice",
            prompt: "What applicant tracking system do you use?",
            subhead: "Where candidates and pipelines live — e.g. Excellent Hiring, Lever, or a spreadsheet.",
            type: "select",
            priority: 4,
            required: true,
            options: [
                { value: "excellent_hiring", label: "Excellent Hiring (this product)" },
                { value: "lever", label: "Lever" },
                { value: "greenhouse", label: "Greenhouse" },
                { value: "ashby", label: "Ashby" },
                { value: "workable", label: "Workable" },
                { value: "spreadsheet", label: "Spreadsheet" },
                { value: "other", label: "Other" },
            ],
        },
        {
            key: "comp_band_eng",
            label: "Engineering comp band",
            prompt: "What's the total comp range for engineering roles?",
            subhead: "Base plus equity range you offer engineers — e.g. \"$140k-$220k base + 0.05-0.5% equity\".",
            placeholder: "e.g. $140k-$220k base + 0.05-0.5% equity",
            type: "longtext",
            priority: 2,
            required: false,
        },
        {
            key: "comp_band_design",
            label: "Design comp band",
            prompt: "What's the total comp range for design roles?",
            subhead: "Base plus equity range you offer designers — same format as the engineering band.",
            type: "longtext",
            priority: 2,
            required: false,
        },
        {
            key: "hiring_lead",
            label: "Hiring lead",
            prompt: "Who owns hiring across the org?",
            subhead: "The workspace member accountable for headcount and the hiring process.",
            type: "urn-person",
            priority: 4,
            required: true,
        },
        {
            key: "recruiting_partners",
            label: "Recruiting partners",
            prompt: "Which external recruiters or agencies do you work with?",
            subhead: "Outside firms that source candidates for you, one per entry. Leave blank if hiring is all in-house.",
            type: "multi",
            priority: 1,
            required: false,
        },
    ],
    seedSets: [
        {
            title: "Set hiring goals",
            questions: [
                { question: "How many people do you want on the team at year end?", subhead: "Your target headcount — a single number.", briefKey: "target_headcount_eoy" },
                { question: "How many people are on the team today?", subhead: "Everyone on payroll right now, including founders.", briefKey: "current_headcount" },
                { question: "What ATS are you using for candidate tracking?", subhead: "Where candidates and pipelines live — e.g. Excellent Hiring, Lever, or a spreadsheet.", briefKey: "ats_choice" },
                { question: "Who is the hiring lead — the person accountable for headcount?", subhead: "Pick the workspace member who owns the hiring process.", briefKey: "hiring_lead" },
                { question: "As a team of one, would bringing on a co-founder or first collaborator accelerate what you're building?", subhead: "Solo today — worth naming the one role or skill a first collaborator would unlock, even if you're not hiring yet.", audience: { maxTeamSize: 1 } },
            ],
        },
    ],
};
// ---------- Development ----------
const DEVELOPMENT = {
    slug: "development",
    label: "Engineering",
    description: "Tech stack, repos, environments, monitoring.",
    seedVersion: 1,
    fields: [
        {
            key: "primary_languages",
            label: "Primary languages",
            prompt: "What languages do you run in production?",
            subhead: "The languages your shipped code is written in — e.g. TypeScript, Python, Go.",
            type: "multi",
            priority: 5,
            required: true,
        },
        {
            key: "primary_frameworks",
            label: "Primary frameworks",
            prompt: "What frameworks does the team commit the most code in?",
            subhead: "Your main application frameworks — e.g. Next.js, Django, Rails.",
            type: "multi",
            priority: 4,
            required: true,
        },
        {
            key: "repo_paths",
            label: "Repo paths",
            prompt: "Where do the repos for this product live on your machine?",
            subhead: "Local folder paths, one per entry — these drive stack auto-detect. e.g. \"/Users/you/code/app\".",
            placeholder: "/Users/you/code/app, /Users/you/code/api",
            type: "multi",
            priority: 5,
            required: true,
        },
        {
            key: "hosting_provider",
            label: "Hosting provider",
            prompt: "Where does production run?",
            subhead: "The platform that hosts your live app — e.g. Vercel, AWS, Fly.io.",
            type: "select",
            priority: 3,
            required: true,
            options: [
                { value: "vercel", label: "Vercel" },
                { value: "netlify", label: "Netlify" },
                { value: "aws", label: "AWS" },
                { value: "gcp", label: "GCP" },
                { value: "azure", label: "Azure" },
                { value: "digitalocean", label: "DigitalOcean" },
                { value: "fly", label: "Fly.io" },
                { value: "render", label: "Render" },
                { value: "railway", label: "Railway" },
                { value: "cloudflare", label: "Cloudflare" },
                { value: "heroku", label: "Heroku" },
                { value: "hetzner", label: "Hetzner" },
                { value: "self_hosted", label: "Self-hosted" },
                { value: "other", label: "Other" },
            ],
        },
        {
            key: "ci_provider",
            label: "CI provider",
            prompt: "Where do your tests and deploys run?",
            subhead: "The CI/CD system that runs on each push — e.g. GitHub Actions, CircleCI.",
            type: "select",
            priority: 3,
            required: true,
            options: [
                { value: "github_actions", label: "GitHub Actions" },
                { value: "circleci", label: "CircleCI" },
                { value: "buildkite", label: "Buildkite" },
                { value: "vercel", label: "Vercel" },
                { value: "gitlab", label: "GitLab CI" },
                { value: "none", label: "None" },
                { value: "other", label: "Other" },
            ],
        },
        {
            key: "environments",
            label: "Environments",
            prompt: "What environments do you ship to, and what are their URLs?",
            subhead: "Each named environment and its address — e.g. \"production: https://app.acme.com\", \"staging: https://staging.acme.com\".",
            placeholder: "production: https://...\nstaging: https://...",
            type: "longtext",
            priority: 3,
            required: true,
        },
        {
            key: "monitoring_tool",
            label: "Monitoring tool",
            prompt: "Where do production errors surface?",
            subhead: "The tool that catches runtime errors and alerts you — e.g. Sentry, Datadog.",
            type: "select",
            priority: 2,
            required: false,
            options: [
                { value: "sentry", label: "Sentry" },
                { value: "datadog", label: "Datadog" },
                { value: "honeycomb", label: "Honeycomb" },
                { value: "new_relic", label: "New Relic" },
                { value: "logs_only", label: "Raw logs only" },
                { value: "none", label: "None" },
                { value: "other", label: "Other" },
            ],
        },
        {
            key: "tech_lead",
            label: "Tech lead",
            prompt: "Who owns architecture decisions?",
            subhead: "The workspace member who makes the final call on technical direction.",
            type: "urn-person",
            priority: 4,
            required: true,
        },
    ],
    seedSets: [
        {
            title: "Map the stack",
            questions: [
                { question: "Where do the repos for this product live on your machine?", subhead: "Local folder paths, one per entry — these drive stack auto-detect.", briefKey: "repo_paths" },
                { question: "Where does production run?", subhead: "The platform that hosts your live app — e.g. Vercel, AWS, Fly.io.", briefKey: "hosting_provider" },
                { question: "Which CI provider runs your tests and deploys?", subhead: "The system that runs on each push — e.g. GitHub Actions, CircleCI.", briefKey: "ci_provider" },
                { question: "What environments do you ship to, and what are their URLs?", subhead: "Each named environment and its address — e.g. production, staging.", briefKey: "environments" },
                { question: "Who is the tech lead — the person who makes architecture calls?", subhead: "Pick the workspace member who owns technical direction.", briefKey: "tech_lead" },
            ],
        },
    ],
};
// ---------- Marketing ----------
const MARKETING = {
    slug: "marketing",
    label: "Marketing",
    description: "Analytics, channels, brand voice, audience.",
    seedVersion: 1,
    fields: [
        {
            key: "analytics_provider",
            label: "Analytics provider",
            prompt: "Where do you measure traffic and behavior?",
            subhead: "The analytics tool you check for visits and conversions — e.g. GA4, PostHog, Plausible.",
            type: "select",
            priority: 5,
            required: true,
            options: [
                { value: "ga4", label: "Google Analytics 4" },
                { value: "posthog", label: "PostHog" },
                { value: "plausible", label: "Plausible" },
                { value: "fathom", label: "Fathom" },
                { value: "mixpanel", label: "Mixpanel" },
                { value: "amplitude", label: "Amplitude" },
                { value: "none", label: "None" },
            ],
        },
        {
            key: "analytics_property_id",
            label: "Analytics property ID",
            prompt: "What's the analytics property or project ID?",
            subhead: "The site/project identifier from your analytics tool — e.g. a GA4 \"G-XXXX\" ID or a PostHog project ID.",
            placeholder: "e.g. G-XXXX or PostHog project id",
            type: "text",
            priority: 4,
            required: true,
        },
        {
            key: "analytics_api_key",
            label: "Analytics API key",
            prompt: "What's the read API key for analytics?",
            subhead: "Used only for connection checks — never to write. Stored privately. Leave blank if you'd rather not connect it.",
            type: "text",
            priority: 3,
            required: false,
            sensitive: true,
        },
        {
            key: "channels",
            label: "Channels",
            prompt: "Which channels actually drive growth?",
            subhead: "The marketing channels you actively invest in — e.g. paid search, SEO, content, social, email.",
            type: "multi",
            priority: 4,
            required: true,
        },
        {
            key: "email_platform",
            label: "Email platform",
            prompt: "What do you send marketing and transactional email through?",
            subhead: "Your email vendor — e.g. Mailchimp, Klaviyo, Resend.",
            type: "select",
            priority: 2,
            required: false,
            options: [
                { value: "mailchimp", label: "Mailchimp" },
                { value: "klaviyo", label: "Klaviyo" },
                { value: "loops", label: "Loops" },
                { value: "customer_io", label: "Customer.io" },
                { value: "resend", label: "Resend" },
                { value: "none", label: "None" },
                { value: "other", label: "Other" },
            ],
        },
        {
            key: "crm_link",
            label: "CRM link",
            prompt: "Which CRM holds your lead data?",
            subhead: "Where contacts and pipeline live — Excellent's CRM, or name the external tool (e.g. HubSpot, Salesforce).",
            type: "text",
            priority: 2,
            required: false,
        },
        {
            key: "site_url",
            label: "Marketing site URL",
            prompt: "Where does your marketing site live?",
            subhead: "The public URL the SEO workbench crawls to read titles, meta, OG tags, sitemap & robots — e.g. https://example.com.",
            placeholder: "https://example.com",
            type: "text",
            priority: 3,
            required: false,
        },
        {
            key: "site_path",
            label: "Marketing site repo / folder",
            prompt: "Where is the site's source on this machine?",
            subhead: "Absolute path to the local repo or folder for the marketing site — lets the SEO workbench read the real page files and apply fixes through ship/verify. e.g. ~/Sites/your-site.",
            placeholder: "~/Sites/your-site",
            type: "text",
            priority: 2,
            required: false,
        },
        {
            key: "brand_colors",
            label: "Brand colors",
            prompt: "What are your primary brand colors?",
            subhead: "Hex codes with a note on each role — e.g. \"#0F172A primary, #6366F1 accent\".",
            placeholder: "#0F172A primary, #6366F1 accent",
            type: "longtext",
            priority: 2,
            required: false,
        },
        {
            key: "brand_voice",
            label: "Brand voice",
            prompt: "How does the brand talk?",
            subhead: "3-5 principles that define your tone — e.g. \"warm but direct\", \"expert without jargon\".",
            type: "longtext",
            priority: 2,
            required: false,
        },
        {
            key: "audience_personas",
            label: "Audience personas",
            prompt: "Who are the 1-3 customer personas you sell to?",
            subhead: "Short sketches of your target buyers — role, context, and the problem you solve for them.",
            type: "longtext",
            priority: 3,
            required: true,
        },
        {
            key: "marketing_lead",
            label: "Marketing lead",
            prompt: "Who owns marketing?",
            subhead: "The workspace member accountable for growth and the marketing function.",
            type: "urn-person",
            priority: 4,
            required: true,
        },
    ],
    seedSets: [
        {
            title: "Connect analytics & audience",
            questions: [
                { question: "Which analytics tool do you use?", subhead: "The tool you check for traffic and conversions — e.g. GA4, PostHog, Plausible.", briefKey: "analytics_provider" },
                { question: "What's the analytics property or project ID?", subhead: "The site/project identifier — e.g. a GA4 \"G-XXXX\" ID.", briefKey: "analytics_property_id" },
                { question: "Which channels actually drive growth — paid, SEO, content, social, email?", subhead: "The channels you actively invest in, one per entry.", briefKey: "channels" },
                { question: "Who are the 1-3 customer personas you sell to?", subhead: "Short sketches of your target buyers — role, context, and problem.", briefKey: "audience_personas" },
                { question: "Who is the marketing lead?", subhead: "Pick the workspace member who owns growth.", briefKey: "marketing_lead" },
            ],
        },
    ],
};
// ---------- Sales (new in v25) ----------
//
// Strategy + policy only. Operational data (leads, opportunities, calls,
// sequences) lives in the CRM product. If a field would be a row in a
// CRM table, it does NOT belong in the brief.
const SALES = {
    slug: "sales",
    label: "Sales",
    description: "ICP, motion, quota, and the GTM playbook.",
    seedVersion: 1,
    fields: [
        {
            key: "icp",
            label: "Ideal customer profile",
            prompt: "Who is your right-fit customer?",
            subhead: "The buyer you win most easily — industry, company size, the role you sell to, and the problem you solve.",
            type: "longtext",
            priority: 5,
            required: true,
        },
        {
            key: "sales_motion",
            label: "Sales motion",
            prompt: "How do deals actually get done?",
            subhead: "The dominant way revenue closes — e.g. founder-led, inbound, outbound, or product-led.",
            type: "select",
            priority: 5,
            required: true,
            options: [
                { value: "founder_led", label: "Founder-led" },
                { value: "inbound", label: "Inbound" },
                { value: "outbound", label: "Outbound" },
                { value: "plg", label: "Product-led growth" },
                { value: "channel", label: "Channel / partner" },
                { value: "mixed", label: "Mixed" },
            ],
        },
        {
            key: "average_deal_size",
            label: "Average deal size",
            prompt: "What's a typical deal worth?",
            subhead: "Your average annual contract value (ACV) — e.g. \"$25,000\".",
            placeholder: "e.g. $25,000",
            type: "text",
            priority: 3,
            required: false,
        },
        {
            key: "sales_cycle_days",
            label: "Sales cycle (days)",
            prompt: "How long does a deal take to close?",
            subhead: "Median days from first touch to signed, as a single number.",
            type: "number",
            priority: 3,
            required: false,
        },
        {
            key: "quota_model",
            label: "Quota model",
            prompt: "How is rep quota and comp structured?",
            subhead: "Per-rep target plus the base/variable split — e.g. \"$800k annual, 50/50 base/variable\".",
            placeholder: "e.g. $800k annual, 50/50 base/variable",
            type: "longtext",
            priority: 3,
            required: false,
        },
        {
            key: "gtm_playbook_url",
            label: "GTM playbook",
            prompt: "Where does your go-to-market playbook live?",
            subhead: "Link to the live doc reps follow — e.g. a Notion page or Google Doc.",
            placeholder: "https://...",
            type: "url",
            priority: 2,
            required: false,
        },
        {
            key: "sales_lead",
            label: "Sales lead",
            prompt: "Who owns sales — the person pipeline and quota roll up to?",
            subhead: "The workspace member accountable for revenue.",
            type: "urn-person",
            priority: 4,
            required: true,
        },
        {
            key: "sales_stack",
            label: "Sales stack",
            prompt: "What tools does the sales team rely on?",
            subhead: "Your CRM, prospecting, and automation tools, one per entry — e.g. Salesforce, Apollo, Outreach.",
            type: "multi",
            priority: 2,
            required: false,
        },
    ],
    seedSets: [
        {
            title: "Lock in the sales motion",
            questions: [
                { question: "Who is your ideal customer profile?", subhead: "Industry, company size, the role you sell to, and the problem you solve.", briefKey: "icp" },
                { question: "Which sales motion drives revenue today?", subhead: "The dominant way deals close — e.g. founder-led, inbound, outbound.", briefKey: "sales_motion" },
                { question: "Who owns sales — pipeline and quota roll up to whom?", subhead: "Pick the workspace member accountable for revenue.", briefKey: "sales_lead" },
                { question: "What is the typical deal size and sales cycle?", subhead: "Your average ACV and median days from first touch to close.", briefKey: "average_deal_size" },
                { question: "You don't have customers yet — which customer types best fit what you're building?", subhead: "Sketch the 2-3 kinds of buyer most likely to need this first, so early outreach has a target.", audience: { hasCustomers: false } },
            ],
        },
    ],
};
// ---------- Customer Success (new in v25) ----------
const CUSTOMER_SUCCESS = {
    slug: "customer-success",
    label: "Support",
    description: "Churn signals, lifecycle moments, NPS, support cadence.",
    seedVersion: 1,
    fields: [
        {
            key: "support_channels",
            label: "Support channels",
            prompt: "How do customers reach you for help?",
            subhead: "The channels you support, one per entry — e.g. email, in-app chat, phone, community.",
            type: "multi",
            priority: 5,
            required: true,
        },
        {
            key: "first_response_target",
            label: "First-response target",
            prompt: "How fast do you aim to first respond?",
            subhead: "Your time-to-first-response SLA — e.g. \"4 business hours\".",
            placeholder: "e.g. 4 business hours",
            type: "text",
            priority: 4,
            required: true,
        },
        {
            key: "nps_target",
            label: "NPS target",
            prompt: "What's your Net Promoter Score target for the year?",
            subhead: "The NPS you're aiming for, as a single number (-100 to 100).",
            audience: { hasCustomers: true },
            type: "number",
            priority: 3,
            required: false,
        },
        {
            key: "csat_cadence",
            label: "CSAT cadence",
            prompt: "How often do you measure customer satisfaction?",
            subhead: "When you survey for CSAT — e.g. after every ticket, or monthly.",
            audience: { hasCustomers: true },
            type: "select",
            priority: 2,
            required: false,
            options: [
                { value: "per_ticket", label: "After every ticket" },
                { value: "weekly", label: "Weekly" },
                { value: "monthly", label: "Monthly" },
                { value: "quarterly", label: "Quarterly" },
                { value: "ad_hoc", label: "Ad hoc" },
            ],
        },
        {
            key: "churn_signals",
            label: "Churn signals",
            prompt: "Which behaviors predict that a customer is about to leave?",
            subhead: "The early warning signs you watch for — e.g. \"login frequency drops\", \"no response to QBR invites\".",
            audience: { hasCustomers: true },
            type: "longtext",
            priority: 4,
            required: true,
        },
        {
            key: "lifecycle_moments",
            label: "Lifecycle moments",
            prompt: "Where in the journey do you intentionally touch the customer?",
            subhead: "The key moments you design for — e.g. onboarding, activation, renewal, expansion.",
            audience: { hasCustomers: true },
            type: "longtext",
            priority: 3,
            required: true,
        },
        {
            key: "cs_lead",
            label: "Customer success lead",
            prompt: "Who owns the customer relationship after the sale?",
            subhead: "The workspace member accountable for retention and expansion.",
            audience: { hasCustomers: true },
            type: "urn-person",
            priority: 4,
            required: true,
        },
        {
            key: "support_tool",
            label: "Support tool",
            prompt: "What helpdesk or ticketing system do you use?",
            subhead: "Where support tickets live — e.g. Zendesk, Intercom, or plain email.",
            type: "select",
            priority: 2,
            required: false,
            options: [
                { value: "zendesk", label: "Zendesk" },
                { value: "intercom", label: "Intercom" },
                { value: "front", label: "Front" },
                { value: "helpscout", label: "Help Scout" },
                { value: "linear", label: "Linear" },
                { value: "email", label: "Plain email" },
                { value: "none", label: "None" },
                { value: "other", label: "Other" },
            ],
        },
    ],
    seedSets: [
        {
            title: "Map the customer journey",
            questions: [
                { question: "How do customers reach you for help today?", subhead: "The channels you support — e.g. email, in-app chat, phone.", briefKey: "support_channels" },
                { question: "What's your first-response target?", subhead: "Your time-to-first-response SLA — e.g. \"4 business hours\".", briefKey: "first_response_target" },
                { question: "Which behaviors predict that a customer is about to churn?", subhead: "The early warning signs you watch for — e.g. usage drops, unanswered check-ins.", briefKey: "churn_signals", audience: { hasCustomers: true } },
                { question: "What are the lifecycle moments where you intentionally touch the customer?", subhead: "The key moments you design for — onboarding, activation, renewal, expansion.", briefKey: "lifecycle_moments", audience: { hasCustomers: true } },
                { question: "Who owns the customer relationship after the sale?", subhead: "Pick the workspace member accountable for retention.", briefKey: "cs_lead", audience: { hasCustomers: true } },
            ],
        },
    ],
};
// ---------- Finance (new in v25) ----------
const FINANCE = {
    slug: "finance",
    label: "Finance",
    description: "Bank, payroll, AR/AP, runway, and the fundraising plan.",
    seedVersion: 1,
    fields: [
        {
            key: "primary_bank",
            label: "Primary bank",
            prompt: "Where does the business bank day-to-day?",
            subhead: "Your main operating bank — e.g. Mercury, Chase, SVB.",
            type: "text",
            priority: 5,
            required: true,
        },
        {
            key: "accounting_software",
            label: "Accounting software",
            prompt: "Where do you keep the books?",
            subhead: "The system of record for your accounting — e.g. QuickBooks, Xero.",
            type: "select",
            priority: 4,
            required: true,
            options: [
                { value: "quickbooks", label: "QuickBooks" },
                { value: "xero", label: "Xero" },
                { value: "freshbooks", label: "FreshBooks" },
                { value: "wave", label: "Wave" },
                { value: "netsuite", label: "NetSuite" },
                { value: "spreadsheet", label: "Spreadsheet" },
                { value: "none", label: "None" },
                { value: "other", label: "Other" },
            ],
        },
        {
            key: "payroll_provider",
            label: "Payroll provider",
            prompt: "Who runs your payroll?",
            subhead: "The provider that pays your team — e.g. Gusto, Rippling, ADP.",
            type: "select",
            priority: 4,
            required: true,
            options: [
                { value: "gusto", label: "Gusto" },
                { value: "rippling", label: "Rippling" },
                { value: "justworks", label: "Justworks" },
                { value: "deel", label: "Deel" },
                { value: "adp", label: "ADP" },
                { value: "paychex", label: "Paychex" },
                { value: "manual", label: "Manual / in-house" },
                { value: "none", label: "None" },
                { value: "other", label: "Other" },
            ],
        },
        {
            key: "ap_cadence",
            label: "AP cadence",
            prompt: "How often do you pay vendors?",
            subhead: "Your accounts-payable rhythm — when bills go out the door.",
            type: "select",
            priority: 2,
            required: false,
            options: [
                { value: "weekly", label: "Weekly" },
                { value: "biweekly", label: "Biweekly" },
                { value: "monthly", label: "Monthly" },
                { value: "net_30", label: "Net 30" },
                { value: "ad_hoc", label: "Ad hoc" },
            ],
        },
        {
            key: "ar_cadence",
            label: "AR cadence",
            prompt: "How often do you invoice and chase receivables?",
            subhead: "Your accounts-receivable rhythm — when invoices go out and follow-ups happen.",
            type: "select",
            priority: 2,
            required: false,
            options: [
                { value: "weekly", label: "Weekly" },
                { value: "biweekly", label: "Biweekly" },
                { value: "monthly", label: "Monthly" },
                { value: "quarterly", label: "Quarterly" },
                { value: "ad_hoc", label: "Ad hoc" },
            ],
        },
        {
            key: "runway_months",
            label: "Runway (months)",
            prompt: "How many months of runway do you have at current burn?",
            subhead: "Months of cash left before you hit zero at today's burn rate, as a single number.",
            type: "number",
            priority: 5,
            required: true,
        },
        {
            key: "fundraising_plan",
            label: "Fundraising plan",
            prompt: "What's the plan for your next raise?",
            subhead: "Timing, target size, lead vs. SAFE, and expected dilution. Leave blank if you're not raising.",
            type: "longtext",
            priority: 3,
            required: false,
        },
        {
            key: "finance_lead",
            label: "Finance lead",
            prompt: "Who owns the numbers?",
            subhead: "The workspace member accountable for the books and financial reporting.",
            type: "urn-person",
            priority: 4,
            required: true,
        },
    ],
    seedSets: [
        {
            title: "Wire up the books",
            questions: [
                { question: "Where does the business bank?", subhead: "Your main operating bank — e.g. Mercury, Chase.", briefKey: "primary_bank" },
                { question: "What accounting software keeps the books?", subhead: "Your system of record for accounting — e.g. QuickBooks, Xero.", briefKey: "accounting_software" },
                { question: "Who runs payroll?", subhead: "The provider that pays your team — e.g. Gusto, Rippling.", briefKey: "payroll_provider" },
                { question: "How many months of runway do you have at current burn?", subhead: "Months of cash left at today's burn rate, as a single number.", briefKey: "runway_months" },
                { question: "Who owns the numbers?", subhead: "Pick the workspace member accountable for the books.", briefKey: "finance_lead" },
            ],
        },
    ],
};
// ---------- Legal (new in v25) ----------
const LEGAL = {
    slug: "legal",
    label: "Legal",
    description: "Entity structure, IP, contract templates, compliance regimes.",
    seedVersion: 1,
    fields: [
        {
            key: "entity_jurisdiction",
            label: "Entity jurisdiction",
            prompt: "Where is the entity incorporated?",
            subhead: "The state or country of incorporation — e.g. \"Delaware, USA\".",
            placeholder: "e.g. Delaware, USA",
            type: "text",
            priority: 5,
            required: true,
        },
        {
            key: "law_firm",
            label: "Law firm of record",
            prompt: "Who is your primary outside counsel?",
            subhead: "The law firm you turn to first. Leave blank if you don't have one yet.",
            type: "text",
            priority: 3,
            required: false,
        },
        {
            key: "ip_policy",
            label: "IP policy",
            prompt: "How do you handle IP assignment and open-source contributions?",
            subhead: "Your stance on invention assignment, employee/contractor IP, and OSS contribution.",
            type: "longtext",
            priority: 4,
            required: true,
        },
        {
            key: "contract_templates",
            label: "Contract templates",
            prompt: "Which contract templates do you reuse?",
            subhead: "Standard agreements you start from, one per entry — e.g. MSA, NDA, employee offer, contractor.",
            type: "multi",
            priority: 3,
            required: false,
        },
        {
            key: "compliance_regimes",
            label: "Compliance regimes",
            prompt: "Which compliance regimes do you operate under?",
            subhead: "Frameworks you must meet, one per entry — e.g. SOC 2, HIPAA, GDPR.",
            type: "multi",
            priority: 4,
            required: true,
        },
        {
            key: "data_retention_policy",
            label: "Data retention policy",
            prompt: "How long do you retain customer, user, and employee data?",
            subhead: "Your retention windows and deletion practice — e.g. \"customer data 7 years, logs 90 days\".",
            type: "longtext",
            priority: 2,
            required: false,
        },
        {
            key: "privacy_policy_url",
            label: "Privacy policy URL",
            prompt: "Where does your live privacy policy live?",
            subhead: "Link to the published privacy policy customers can read.",
            placeholder: "https://...",
            type: "url",
            priority: 3,
            required: false,
        },
        {
            key: "terms_url",
            label: "Terms of service URL",
            prompt: "Where do your live terms of service live?",
            subhead: "Link to the published terms customers agree to.",
            placeholder: "https://...",
            type: "url",
            priority: 3,
            required: false,
        },
        {
            key: "legal_lead",
            label: "Legal lead",
            prompt: "Who internally owns legal?",
            subhead: "The workspace member accountable for legal, even if outside counsel does most of the work.",
            type: "urn-person",
            priority: 4,
            required: true,
        },
    ],
    seedSets: [
        {
            title: "Establish the legal posture",
            questions: [
                { question: "Where is the entity incorporated?", subhead: "The state or country of incorporation — e.g. \"Delaware, USA\".", briefKey: "entity_jurisdiction" },
                { question: "What's the IP assignment and open-source policy?", subhead: "How invention assignment, employee/contractor IP, and OSS contribution are handled.", briefKey: "ip_policy" },
                { question: "Which compliance regimes do you operate under?", subhead: "Frameworks you must meet — e.g. SOC 2, HIPAA, GDPR.", briefKey: "compliance_regimes" },
                { question: "Who internally owns legal?", subhead: "Pick the workspace member accountable for legal.", briefKey: "legal_lead" },
            ],
        },
    ],
};
// ---------- Catalog ----------
/**
 * Canonical catalog keyed by section slug. Insertion order matches the
 * rail order users see (legacy 5 first, then the 4 new canonical
 * sections in opt-in order: Sales / CS / Finance / Legal).
 */
export const SECTION_BRIEF_CATALOG = {
    operations: OPERATIONS,
    design: DESIGN,
    hiring: HIRING,
    development: DEVELOPMENT,
    marketing: MARKETING,
    sales: SALES,
    "customer-success": CUSTOMER_SUCCESS,
    finance: FINANCE,
    legal: LEGAL,
};
export const SECTION_BRIEF_SLUGS = Object.keys(SECTION_BRIEF_CATALOG);
/**
 * Canonical registry — single source of truth for `{slug, name,
 * autofillSupported, legacy, briefSpec, seedSets}` per section.
 * Insertion order = rail order.
 */
export const SECTION_REGISTRY = [
    {
        slug: "operations",
        name: "Operations",
        autofillSupported: true,
        legacy: true,
        briefSpec: OPERATIONS,
        seedSets: OPERATIONS.seedSets,
    },
    {
        slug: "design",
        name: "Design",
        autofillSupported: true,
        legacy: true,
        briefSpec: DESIGN,
        seedSets: DESIGN.seedSets,
    },
    {
        slug: "hiring",
        name: HIRING_DISPLAY_NAME,
        autofillSupported: true,
        legacy: true,
        briefSpec: HIRING,
        seedSets: HIRING.seedSets,
    },
    {
        slug: "development",
        name: "Engineering",
        autofillSupported: true,
        legacy: true,
        briefSpec: DEVELOPMENT,
        seedSets: DEVELOPMENT.seedSets,
    },
    {
        slug: "marketing",
        name: "Marketing",
        autofillSupported: true,
        legacy: true,
        briefSpec: MARKETING,
        seedSets: MARKETING.seedSets,
    },
    {
        slug: "sales",
        name: "Sales",
        autofillSupported: false,
        legacy: false,
        briefSpec: SALES,
        seedSets: SALES.seedSets,
    },
    {
        slug: "customer-success",
        name: "Support",
        autofillSupported: false,
        legacy: false,
        briefSpec: CUSTOMER_SUCCESS,
        seedSets: CUSTOMER_SUCCESS.seedSets,
    },
    {
        slug: "finance",
        name: "Finance",
        autofillSupported: false,
        legacy: false,
        briefSpec: FINANCE,
        seedSets: FINANCE.seedSets,
    },
    {
        slug: "legal",
        name: "Legal",
        autofillSupported: false,
        legacy: false,
        briefSpec: LEGAL,
        seedSets: LEGAL.seedSets,
    },
];
const REGISTRY_BY_SLUG = Object.fromEntries(SECTION_REGISTRY.map((e) => [e.slug, e]));
export function getSectionSpec(slug) {
    return SECTION_BRIEF_CATALOG[slug] ?? null;
}
export function getSectionRegistryEntry(slug) {
    return REGISTRY_BY_SLUG[slug] ?? null;
}
/**
 * Slugs of the original five canonical sections — Operations, Design,
 * Hiring, Development, Marketing. Used by `ensureProjectSections` to
 * decide which sections an EXISTING project already has rows for.
 */
export const LEGACY_SECTION_SLUGS = SECTION_REGISTRY
    .filter((e) => e.legacy)
    .map((e) => e.slug);
/**
 * The eight business sections hidden in Phase 2 (M2). Slugs stay in the
 * registry so gates can still resolve them; they insert with `enabled=0`
 * and the workspace-tier default is off. Development / Engineering stays.
 */
export const BUSINESS_SECTION_SLUGS = [
    "operations",
    "design",
    "hiring",
    "marketing",
    "sales",
    "customer-success",
    "finance",
    "legal",
];
/** Default `sections.enabled` / workspace-tier on-ness for a registry slug. */
export function defaultSectionEnabled(slug) {
    return slug === "development";
}
/**
 * Required field count for a section. Drives the denominator of the
 * completeness score.
 */
export function requiredFieldCount(slug) {
    const spec = getSectionSpec(slug);
    if (!spec)
        return 0;
    return spec.fields.filter((f) => f.required).length;
}
/**
 * Seed key used in the `_seeds` table for idempotent application.
 */
export function sectionSeedKey(slug) {
    return `section_brief:${slug}`;
}
